import React from 'react'
import { useGetBusiness } from '@marketing-milk/frontend'
import {
  AdCreative,
  CampaignSchedule,
  PublishCampaignDTO,
  SavedAudience,
} from '@marketing-milk/interfaces'
import { Step } from 'components/Stepper'
import { Stepper } from 'components/Stepper/Stepper'
import { all, any, equals, set } from 'lodash/fp'
import { useEffect, useState } from 'react'
import { campaignService } from '../../../../app/services'
import { rejectNil } from '../../../../app/util'
import {
  CampaignAdStep,
  CampaignAdsetsStep,
  CampaignReviewStep,
  CampaignSettingsStep,
} from '../forms/steps'
import * as H from '../helpers/campaignForm.helpers'
import {
  CampaignFormErrors,
  campaignDtoHasValidCreatives,
  singleCampaignDtoIsValid,
} from '../helpers/campaignForm.helpers'

type Props = {
  audiences?: SavedAudience[]
  dto?: PublishCampaignDTO
  /**
   * The campaign exists on Meta, so its objective and performance goal are locked. Fed from
   * `Campaign.status`, which only exists once a campaign has published successfully.
   */
  isLiveOnFacebook?: boolean
  onSubmit: (dto: PublishCampaignDTO) => void
  campaignErrors?: CampaignFormErrors
  // the caller's submit creates a new Meta campaign from this DTO rather than republishing one
  canPublishPaused?: boolean
}

export type AdCreativeGroup = (AdCreative | undefined)[]

export const CampaignForm = ({ dto, audiences, isLiveOnFacebook, ...props }: Props) => {
  const [selectedCreatives, setSelectedCreatives] = useState<AdCreative[]>([])
  const [errors, setErrors] = useState<CampaignFormErrors>({
    campaign: props.campaignErrors?.campaign,
    adsets: props.campaignErrors?.adsets,
    ads: props.campaignErrors?.ads,
  })
  const [schedule, setCampaignSchedule] = useState<CampaignSchedule>()

  const business = useGetBusiness()

  const [campaignDto, setCampaignDto] = useState<PublishCampaignDTO | undefined>(
    H.getDefaultDTO(dto, business.timezone)
  )

  useEffect(() => {
    if (!dto) return
    setCampaignDto(dto)
  }, [])

  useEffect(() => {
    if (!props.campaignErrors) return
    setErrors(props.campaignErrors)
  }, [dto])

  const handleSubmit = async () => {
    if (!campaignDto) return
    if (!singleCampaignDtoIsValid(campaignDto, setErrors)) return
    if (!campaignDtoHasValidCreatives(campaignDto, selectedCreatives)) {
      setErrors(set('ads', ['Selected creatives are invalid or missing']))
      return
    }

    props.onSubmit(campaignDto)
  }

  const fetchCampaignSchedule = async (numOfCreativeGroups: number) => {
    if (campaignDto && numOfCreativeGroups > 1) {
      const generatedSchedule = await campaignService.getCampaignSchedule(business.id, {
        startTime: campaignDto.campaign.startTime,
        endTime: campaignDto.campaign.endTime,
        numOfAdGroups: numOfCreativeGroups,
      })
      setCampaignSchedule(generatedSchedule)
    }
  }

  useEffect(() => {
    if (!dto) return
    fetchCampaignSchedule(dto.creativeGroups.length)
  }, [dto?.campaign, dto?.creativeGroups])

  return (
    <div style={{ width: '80vw' }}>
      <Stepper hideSubmit>
        {/* Select Settings */}
        <Step
          title={`${campaignDto?.campaign?.name || 'Campaign'} Settings`}
          completed={all(equals(true), H.campaignSettingsValidators(campaignDto))}
          disabled={any(equals(false), H.campaignSettingsValidators(campaignDto))}
          errors={errors.campaign}
          onNext={() => setErrors(set('campaign', undefined))}
        >
          <CampaignSettingsStep
            isEdit={!!dto}
            isPublished={!!dto}
            canPublishPaused={props.canPublishPaused}
            isLiveOnFacebook={isLiveOnFacebook}
            campaignDto={campaignDto}
            setCampaignDto={setCampaignDto}
            errors={errors.campaign}
          />
        </Step>

        {/* Select Audiences */}
        <Step
          title="Adsets"
          completed={H.audiencesAreValid(campaignDto?.audienceIDs)}
          disabled={!H.audiencesAreValid(campaignDto?.audienceIDs)}
          errors={errors.adsets}
          onNext={() => setErrors(set('adsets', undefined))}
        >
          <CampaignAdsetsStep
            title="Adsets"
            campaignDto={campaignDto}
            setCampaignDto={setCampaignDto}
            audiences={audiences}
            errors={errors.adsets}
          />
        </Step>

        {/* Select Ads */}
        <Step
          title="Ads"
          completed={campaignDto && !!rejectNil(campaignDto?.creativeGroups[0])?.length}
          disabled={campaignDto && !rejectNil(campaignDto?.creativeGroups[0])?.length}
          errors={errors.ads}
          onNext={() => setErrors(set('ads', undefined))}
        >
          {campaignDto && (
            <CampaignAdStep
              campaignDto={campaignDto}
              setCampaignDto={setCampaignDto}
              schedule={schedule}
              updateSchedule={fetchCampaignSchedule}
              onSelectedCreativesChange={setSelectedCreatives}
            />
          )}
        </Step>

        {/* Review Campaign */}
        <Step title="Review">
          {campaignDto && (
            <CampaignReviewStep
              campaignDto={campaignDto}
              setCampaignDto={setCampaignDto}
              onPublish={handleSubmit}
              audiences={audiences}
              creatives={selectedCreatives}
              schedule={schedule}
            />
          )}
        </Step>
      </Stepper>
    </div>
  )
}
